import { AssetSelection } from "../../components/v2/lti/AssetSelection";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "../../services/axios";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { CourseStructureCtx, IBackBehavior } from "../../interfaces";
import {
  Backdrop,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { ReactComponent as ArtWork } from "../../assets/LTI-deeplink-artwork.svg";
import { SuccessToast } from "../../components/v2/utils/Toast";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";

export const LtiDeepLinkingPage = () => {
  const history = useHistory();
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const formRef = useRef<HTMLFormElement>(null);
  const [formAction, setFormAction] = useState<string>("");
  const [ltiParamsWithSignature, setLtiParamsWithSignature] = useState<string>("");
  const [hasSetBackBehavior, setHasSetBackBehavior] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasDeepLinked, setHasDeepLinked] = useState<boolean>(false);
  const [openCourseSelector, setOpenCourseSelector] = useState<boolean>(true);

  const location = useLocation();
  let queryParams = new URLSearchParams(location.search);
  const [ltik] = useState(MacsAuthenticator.authHook.ltik ?? "");
  const [clientId] = useState(queryParams.get("clientId") || "");
  const [courseId] = useState<string>(queryParams.get("courseId") || "1");
  const [allCourses, setAllCourses] = useState([]);

  console.log(">>>>>>>>>>>>>ltik", ltik);
  const DeepLinkAsset = async (assetId: string, assetType: string, title: string) => {
    if (!hasDeepLinked) {
      setHasDeepLinked(true);
      setLoading(true);
      try {
        let response = await axios.post(`/lti/1p3/deeplink?ltik=${ltik}`, {
          assetId: assetId,
          assetType: assetType,
          title: title,
          courseId: courseId,
        });
        setFormAction(response.data.formAction);
        setLtiParamsWithSignature(response.data.ltiParamsWithSignature);
      } catch (e) {
        console.log(e);
        setHasDeepLinked(false);
      }
      setLoading(false);
    } else {
    }
  };

  // this will force the back behavior to return to deep linking page
  // in the event that user previews and tries to return
  // only triggering useEffect once so that we don't update backBehavior accidentally when search params are cleared
  // the deeplinkMode contains the ltik, courseId, and clientId that would otherwise be lost if the backBehavior changes
  useEffect(() => {
    if (!hasSetBackBehavior) {
      const backTo: IBackBehavior = {
        backTo: "deeplinking",
        route: "/lti/deep-linking",
        search: new URLSearchParams({
          ltik: `${ltik}`,
          courseId: `${courseId}`,
          clientId: `${clientId}`,
        }).toString(),
      };
      courseStructureCtx.setDeeplinkMode(backTo);
      courseStructureCtx.setBackBehavior(backTo);
      setHasSetBackBehavior(true);
    }
  }, [hasSetBackBehavior, courseStructureCtx, courseId, ltik, clientId]);

  useEffect(() => {
    const formElement = formRef.current;
    if (formElement && formElement.action !== "" && ltiParamsWithSignature !== "") {
      formElement.submit();
    }
  }, [ltiParamsWithSignature]);

  useEffect(() => {
    const getCourseList = async () => {
      if (!clientId) return;
      try {
        let response = await axios.get(`/courses?client_id=${clientId}`);
        setAllCourses(response.data);
      } catch (e) {
        console.log(e);
      }
    };
    getCourseList();
  }, [clientId]);

  const navigateToNewCourse = useCallback(
    (courseId: string) => {
      history.push({
        pathname: "/lti/deep-linking",
        search: new URLSearchParams({
          ltik: `${ltik}`,
          courseId: `${courseId}`,
          clientId: `${clientId}`,
        }).toString(),
      });
      //window.location.reload();
    },
    [clientId, history, ltik]
  );

  useEffect(() => {
    const prefferedCourseId = localStorage.getItem("courseId");
    if (prefferedCourseId) {
      setOpenCourseSelector(false);
      if (prefferedCourseId !== courseId) {
        navigateToNewCourse(prefferedCourseId);
      }
    }
  }, [courseId, navigateToNewCourse]);

  const handleChange = (event: SelectChangeEvent) => {
    console.log("event.target.value", event.target.value);
    localStorage.setItem("courseId", event.target.value as string);
    navigateToNewCourse(event.target.value as string);
    let message = (
      <>
        <Typography component={"p"} variant={"h6"}>
          Course Settings saved
        </Typography>
        <Typography sx={{ marginTop: "8px" }} component={"p"}>
          Switch courses any time at the top of this page and we'll automatically save it
          as a preference
        </Typography>
      </>
    );
    SuccessToast(message);
  };

  return (
    <>
      {openCourseSelector ? (
        <Box
          sx={{
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            height: "100%",
          }}>
          <Stack
            direction={"column"}
            gap={"16px"}
            alignContent={"center"}
            alignItems={"center"}>
            {/* TODO artwork needs to be changed */}
            <ArtWork />
            <Typography variant={"h3"}>Course settings</Typography>
            <Typography variant={"body1"} sx={{ fontSize: "18px" }}>
              Please select the course related to your class.
            </Typography>
            <FormControl sx={{ width: "260px" }}>
              <InputLabel id="demo-simple-select-label">Select Course</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={courseId}
                label="Select Course"
                onChange={handleChange}>
                {allCourses.map((course: any) => {
                  return (
                    <MenuItem key={course.id} value={course.id}>
                      {course.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      ) : (
        <>
          <Stack
            sx={{ paddingLeft: "32px", paddingTop: "32px" }}
            direction="row"
            spacing={"32px"}
            alignItems={"center"}>
            <Typography variant={"h3"}>
              {courseStructureCtx.courseTreeStructure?.getCurrentCourse().name}
            </Typography>
            <Typography
              onClick={() => {
                setOpenCourseSelector(true);
              }}
              sx={{ textDecoration: "underline", cursor: "pointer" }}
              variant={"body2"}>
              Switch courses
            </Typography>
          </Stack>
          <AssetSelection DeepLinkAsset={DeepLinkAsset} hasDeepLinked={hasDeepLinked} />
        </>
      )}
      <div>
        <form ref={formRef} id="ltiLaunchForm" action={formAction} method="POST">
          <input type="hidden" name="JWT" value={ltiParamsWithSignature} />
        </form>
      </div>
      {loading && (
        <Backdrop open sx={{ zIndex: 100000 }}>
          <CircularProgress color="primary" />
        </Backdrop>
      )}
    </>
  );
};
